// Shared hook that pipes the active-call SSE client into a
// `midCall` signal shaped for `resolveCompanionState`.
//
// Each of the six R9 write-heavy review screens calls this hook with
// its current customerId. It:
//
//   1. Reads the ActiveCallContext (provider at app bootstrap)
//   2. Subscribes to the per-rep SSE stream and filters to the
//      currently viewed customerId
//   3. Returns either a `midCall: {label, actionName}` or `null`
//
// The returned signal is merged with any prop-provided
// `companionSignals.midCall` from the test seam; the test-provided
// signal wins so unit tests remain hermetic.

import { useMemo } from 'react';
import type { ScreenCompanionSignals } from './useScreenCompanionState.js';
import { useActiveCall } from '../../hooks/useActiveCall.js';
import { useActiveCallContext } from '../../realtime/active-call-context.js';

export interface UseActiveCallSignalOptions {
  customerId: string | null;
  /** Pre-formatted mid-call label the screen wants rendered. */
  label: string;
  /** Optional actionName used by the DataBoundary chrome (set to the
   * vendor action id when the screen's primary write is Kixie-attached). */
  actionName?: string;
}

export interface MidCallSignal {
  label: string;
  actionName?: string;
}

/**
 * Returns a shaped mid-call signal (or null) based on the live SSE
 * subscription. When the ActiveCallContext has no client the hook
 * returns null without opening a connection.
 */
export function useActiveCallSignal(
  options: UseActiveCallSignalOptions,
): MidCallSignal | null {
  const ctx = useActiveCallContext();
  const customerIdForFilter = options.customerId ?? null;
  const args = useMemo(
    () => (ctx.projectId && ctx.repId
      ? { projectId: ctx.projectId, repId: ctx.repId }
      : null),
    [ctx.projectId, ctx.repId],
  );
  const { isActiveForCustomer } = useActiveCall(
    ctx.client,
    args,
    { customerIdForFilter },
  );
  if (!isActiveForCustomer) return null;
  return options.actionName
    ? { label: options.label, actionName: options.actionName }
    : { label: options.label };
}

/**
 * Convenience: merge a prop-provided `companionSignals` with a live
 * mid-call signal. The prop signal wins so tests stay deterministic.
 */
export function mergeMidCallSignal(
  propSignals: ScreenCompanionSignals | undefined,
  live: MidCallSignal | null,
): ScreenCompanionSignals {
  const base = propSignals ?? {};
  if (base.midCall) return base;
  if (!live) return base;
  return { ...base, midCall: live };
}
